import React from "react";
import { Link } from "react-router-dom";
import { Calculator } from "lucide-react";
import CTALarge from "../CTA/CTALarge.jsx";
import Seo from "../Seo.jsx";
import Tooltip from "./Tooltip";
import "./guides.css";
import guidesConfig from "../../config/guidesConfig.js";

const EquivalentBoostedOddsGuide = () => {
  const meta = guidesConfig.equivalentBoostedOddsGuide?.seo || {};

  return (
    <div className="guides-content">
      <Seo
        title={meta.title}
        description={meta.description}
        url={guidesConfig.eboGuide?.route || "/guides/ebo"}
      />
        <h6 className="info">Team Matched Edge developed this approach, and we're very open to your feedback. You can <a href="mailto:matchededge@gmail.com">contact us here</a>.</h6>
      <p>
        Risk-free bets are a staple in matched betting. Most of the time, we lay
        them for a small, guaranteed profit. But what if you can’t lay the bet
        — or the value is strong enough to let it ride? That’s where{" "}
        <Tooltip content="Odds adjusted to reflect the value of a free bet">
          Equivalent Boosted Odds (EBO)
        </Tooltip>{" "}
        come in. It helps you decide if a risk-free bet is worth taking as an{" "}
        <Tooltip content="Placing a bet with positive expected value">
          advantage play (AP)
        </Tooltip>
        .
      </p>
     

      <h2>What Is a Risk-Free Bet?</h2>
      <p>
        Risk-free bet offers are bookmaker promotions that protect your initial stake.
        If your qualifying bet loses, the bookmaker refunds your stake as a free bet, a bonus or as cash.
      </p>
      <CTALarge
        to="/guides/risk-free-bet-offers-guide"
        text="Learn About Basic Risk-Free Bet Offers"
        icon={Calculator}
      />

      <h2>What Are Equivalent Boosted Odds?</h2>
    <p>Most matched bettors lay the bet to lock in profit. It’s safe and simple.</p>
      <p>But sometimes:</p>
      <ul>
        <li>You <strong>can’t lay</strong> (e.g. obscure markets, <Link to="/guides/how-to-lay-bet-builders">bet builders</Link>).</li>
        <li>The offer has <strong>high value</strong> and you think it’s worth the risk.</li>
      </ul>
      <p>So then we have two options...</p>
    <ul>
      <li>Lay it (if possible) using matched betting for a small guaranteed profit.</li>
      <li>Take it as an <Tooltip content="Advantage Play – placing a bet with positive expected value">AP bet</Tooltip> and hope to win, accepting some variance.</li>
    </ul>

    <p>The challenge? How do we know when AP is the better choice?</p>

    <p>
      Instead of just looking at <Tooltip content="Expected Value – the average outcome over time">EV</Tooltip>, we can reframe the bet like a <a href="https://www.oddsmonkey.com/matched-betting/value-betting/" target="_blank" rel="noreferrer">value bet</a> by calculating the{" "}
      <Tooltip content="Equivalent Boosted Odds – the odds you’d need on a normal bet to match the value of your risk-free offer">
      Equivalent Boosted Odds
      </Tooltip>.
    </p>
    <h6 className="info">
          Equivalent Boosted Odds (EBO) reframes risk‐free bet offers as value bets by converting the risk-free aspect of the bet into an odds boost.
        </h6>
        <p>
          In other words, treating a risk-free bet offer as Advantage Play is equivalent to punting a bet at greatly improved odds.
        </p>
        
    <p>This lets us answer a simple question:</p>
    <h6 className="info">“Would I take this bet if these were the real odds?”</h6>

    <p>
      If the boosted odds are higher than the true odds, AP is the better choice.
    </p>
      <p>
        <Tooltip content="Odds you’d need on a normal bet to match the value of a free one">
          Equivalent Boosted Odds
        </Tooltip>{" "}
        show what the odds would be if your risk-free bet were a normal
        bet. If those odds exceed the true odds, you have a value play.
      </p>
      <p>If you like, we are saying <em>"If I physically risk only £X, I’d need odds of <strong>EBO</strong> to make this into a plain bet.”</em></p>
      <h6 className="warn">It's important to understand that although bookies call them "risk-free", when we treat such offers as <span className="key">advantage play</span>, we will still lose any value we can't retain from our free bet. Unless our stake is returned as cash, if our first bet loses, our stake is not returned in any winnings from the free bet.
        </h6>

      <h2>Equivalent Boosted Odds (EBO) Formula</h2>
      <pre>
        <code>
        EBO = (Stake × Bookie Odds) ÷ (Stake − Effective Free Bet)        
        </code>
      </pre>
        <p>Don't worry... We built a calculator!</p>
      <div>
      <CTALarge
        to="/calculators/risk-free-ebo"
        text="Go to the EBO Calculator"
        icon={Calculator}
      />
        <div className="example-container">

       
<h4>Example</h4>  
<p>Let’s say we have a £10 risk‑free bet where:</p>
  <ul>
    <li>
      If we lose, we get a{" "}
      <Tooltip content="70% of the free bet stake is returned in value">
        £10 free bet with estimated 70% retention
      </Tooltip>{" "}
      (worth £7).
    </li>
    <li>The bookie odds are <strong>4.0</strong>.</li>
  </ul>
  <p>We’ll now work out the boosted odds in four quick steps.</p>

  <ol>
    <li>
      <strong>Calculate Potential Profit</strong>
      <p>
        If the bet wins: <code>£10 × (4 − 1) = £30</code>, so potential profit ={" "}
        <strong>£30</strong>.
      </p>
    </li>
    <li>
      <strong>Calculate Maximum Loss</strong>
      <p>
        If it loses: you get £7 back, so loss = <code>£10 − £7 = £3</code> (max
        loss = <strong>£3</strong>).
      </p>
      <h6 className="info">You can also calculate <span className="key">Implied Odds</span> at this point, as you would with <Link to="/guides/extra-place-offers-guide">targeting extra places</Link>.</h6>
    </li>
    <li>
      <strong>Calculate Boosted Odds</strong>
      <p>Plug into the formula:</p>
      <pre>
      <code>EBO = (10 × 4) ÷ (10 − 7) = 40 ÷ 3 ≈ 13.33</code>      
      </pre>
      <h6 className="info">Effectively odds of <strong>13.33</strong> instead of 4.0.</h6>
    </li>
    <li>
      <strong>Compare to True Odds: AP or lay?</strong>
      <p>
        If true odds &lt; 13.33 → AP bet.  
        If true odds &gt; 13.33 → Lay the bet.
      </p>
    </li>
  </ol>
  </div>
<h2>Rating the bet</h2>  
<p>Use <code>(EBO ÷ bookie odds) × 100</code> to convert to a <span className="key">value bet</span> rating.</p>
<p>Risk-free bets will often (but not always) yield enormous ratings and far exceed most people's benchmarks for good value betting opportunities.</p>

</div>


    

      <h2>Is It Worth the Risk?</h2>
      <p>
        Compare the <span className="key">Equivalent Boosted Odds</span> (EBO) to the <strong>true odds</strong>. In theory:
      </p>
      <ul>
        <li>EBO &gt; true odds → Let it ride (+EV)</li>
        <li>EBO &lt; true odds → Lay it</li>
      </ul>
      <p>In practice, of course, marginal gains are not often worth the extra risk. It will often be better to lay, if possible, in marginal cases. This will depend on your risk tolerance.</p>
      <h6 className="info">
        Even when you can’t lay, EBO tells you if placing the bet makes sense.
      </h6>
      <p>
          Use sharp odds or a conservative guess for your true odds.
        </p>

        <div>
  <h2>EBO vs Implied Odds</h2>
  <p>
    When you use a free bet, you can look at{" "}
    <a href="https://www.oddsmonkey.com/blog/matched-betting/implied-odds/" target="_blank" rel="noreferrer">Implied Odds</a>{" "}
    or{" "}
    <Tooltip content="Effective odds you’re getting when you factor in the bookie’s price">
      Equivalent Boosted Odds (EBO)
    </Tooltip>
    . They answer different questions:
  </p>
  <ul>
    <li>
      <strong>Implied odds</strong> tell you the break‑even price.
      <pre>
        <code>
          Implied odds = (Potential profit ÷ Qualifying loss) + 1
        </code>
      </pre>
    </li>
    <li>
      <strong>Equivalent Boosted Odds (EBO)</strong> show the boosted price when
      you factor in the bookie’s odds.
      <pre>
        <code>
          EBO = (Stake × Bookie odds − Free bet value) ÷ (Stake − Free bet value)
        </code>
      </pre>
    </li>
  </ul>
            <div className="example-container">
                <h4>Example</h4>
                <p>
                    Suppose you place a £10 risk‑free bet at odds of 2.0, and if it loses you
                    receive a free bet worth £8. That means:
                </p>
                <p>
                    – If it wins, your profit is £10 × (2.0 − 1) = £10.<br />
                    – If it loses, your net loss is £10 − £8 = £2.
                </p>
                <p>Now let’s compare implied odds and EBO:</p>
                <pre>
                    <code>
                    {/* Implied odds: break‑even price */}
                    Implied odds = (10 ÷ 2) + 1 = 6.0{"\n\n"}
                    {/* EBO: effective boosted odds */}
                    EBO = (10 × 2) ÷ (10 − 8) = 20 ÷ 2 = 10.0
                    </code>
                </pre>
                <p>
                    The <strong>implied odds</strong> of 6.0 tell you the minimum price you
                    need just to break even. The <strong>EBO</strong> of 10.0 shows the
                    “boosted” odds you’re effectively betting at, compared to the bookie’s
                    advertised 2.0.
                </p>


            </div>

  <p>
    Use <strong>implied odds</strong> to check you won’t lose.
    Use <strong>EBO</strong> to spot real value and decide if an AP bet beats laying.
  </p>
</div>



      <h2>That's it!</h2>
      <p>
        With Equivalent Boosted Odds, you can
        spot high-value AP bets — even when you can’t lay. Try the maths and see
        for yourself.
      </p>
      <CTALarge
        to="/calculators/risk-free-ebo"
        text="Try the EBO Calculator"
        icon={Calculator}
      />
    </div>
    
  );

  
};

export default EquivalentBoostedOddsGuide;
